import { useState } from 'react';
import { useDispatch } from 'react-redux';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import Popper from '@components/Popper';
import { Checkbox } from '@components/Checkbox';
import Button from '@components/Button';
import { MenuTinyArrow } from '@components/MenuTinyArrow';
import { Typography } from '@components/Typography';
import { handleApiError } from '@helpers/api';
import { AssignedStatus } from '@constants/conversations';
import { useTranslation } from '@hooks';
import { alertActions, conversationsActions } from '@actions';
import { conversationsService } from '@services';
import EditBulkModal from './EditBulkModal';
import { ConversationsToolbar } from './styles';

const SelectedDialogsToolbar = ({ selectedConversations, setSelectedConversations, counters, filter }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const handleOpenEl = event => setAnchorEl(event.currentTarget);

    const handleCloseEl = () => setAnchorEl(null);

    const onSelectAll = () => {
        setSelectedConversations(prev => {
            const allSelected = prev.has('all');
            return allSelected ? new Map() : new Map([['all', true]]);
        });
    };

    const exportAsCSV = async () => {
        try {
            await conversationsService.exportCSV({
                selectedConversations: [...selectedConversations.keys()].filter(id => id !== 'all'),
                selectByFilter: selectedConversations.has('all'),
                filter,
            });
            dispatch(alertActions.success(t('InboxPage.exportInProgress')));
            setSelectedConversations(new Map());
        } catch (error) {
            handleApiError(error);
        } finally {
            handleCloseEl();
        }
    };

    const onEditBulk = async values => {
        // INFO: values - property, status
        try {
            await conversationsService.editBulk({
                selectedConversations: [...selectedConversations.keys()].filter(id => id !== 'all'),
                selectByFilter: selectedConversations.has('all'),
                filter,
                ...values,
            });
            dispatch(conversationsActions.getCounters(filter));
            dispatch(conversationsActions.get(filter));
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            setSelectedConversations(new Map());
        } catch (error) {
            handleApiError(error);
        } finally {
            handleCloseEl();
            setEditDialogOpen(false);
        }
    };

    const CounterMap = {
        [AssignedStatus.Mine]: counters?.assigned,
        [AssignedStatus.Unassigned]: counters?.unassigned,
        [AssignedStatus.All]: counters?.all,
        [AssignedStatus.Assigned]: counters?.filtered,
    };

    const allSelected = selectedConversations.has('all');
    const selectedCount = allSelected ? CounterMap[filter.assignment] : selectedConversations.size;

    return (
        <ConversationsToolbar>
            <Typography
                variant="caption"
                sx={{
                    color: '#1F4C5C',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'fit-content',
                    cursor: 'pointer',
                    overflow: 'hidden',
                }}
                onClick={onSelectAll}
            >
                <Checkbox checked={allSelected} indeterminate={!allSelected} />
                <span style={{ marginLeft: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t('InboxPage.conversationSelected', { count: selectedCount })}
                </span>
            </Typography>
            {anchorEl && (
                <ClickAwayListener onClickAway={handleCloseEl}>
                    <Popper anchorEl={anchorEl} open={!!anchorEl}>
                        <MenuItem onClick={() => setEditDialogOpen(true)}>
                            <EditIcon
                                sx={{ height: '18px', width: '18px', color: 'rgba(0, 0, 0, 0.6)', marginRight: '8px' }}
                            />
                            <Typography variant="body2">{t('InboxPage.editConversations')}</Typography>
                        </MenuItem>
                        <MenuItem onClick={exportAsCSV}>
                            <FileDownloadIcon
                                sx={{ height: '18px', width: '18px', color: 'rgba(0, 0, 0, 0.6)', marginRight: '8px' }}
                            />
                            <Typography variant="body2">{t('buttons.exportAsCSV')}</Typography>
                        </MenuItem>
                    </Popper>
                </ClickAwayListener>
            )}
            <Button
                variant="text"
                sx={{
                    fontSize: '0.875rem',
                    lineHeight: '16px',
                    letterSpacing: '0.1px',
                    height: '24px',
                    padding: '4px',
                    minWidth: 'initial',
                }}
                onClick={handleOpenEl}
            >
                {t('common.actions')}
                <MenuTinyArrow isUp={!!anchorEl} />
            </Button>
            {editDialogOpen ? (
                <EditBulkModal
                    open={editDialogOpen}
                    setOpen={setEditDialogOpen}
                    selectedCount={selectedCount}
                    onSubmit={onEditBulk}
                />
            ) : null}
        </ConversationsToolbar>
    );
};

export default SelectedDialogsToolbar;
